import { Celebration } from "../../calendar/ordinariate-calendar";
import { CalendarDay } from "../../calendar/utils";
import { getPointedCanticle } from "../../chants/utils";
import { getOpeningResponses } from "../../liturgy/opening-responses";
import { getOpeningSentence } from "../../liturgy/opening-sentence";
import { getSalutation } from "../../liturgy/salutation";
import { getSuffrages } from "../../liturgy/suffrages";
import { LiturgyItem } from "../../liturgy/types";
import { PointedText } from "../../models";
import { LoadableDataDependency } from "../../utils/RemoteDataProvider";
import { LessonData, Settings } from "../../utils/settings";
import { NewHymnData } from "./NewHymn";
import { RESURRECTION_GOSPEL } from "./resurrection-chant";

export interface OrdinariateLessons {
    lessons: {
        morningFirstLesson: LessonData;
        morningSecondLesson: LessonData;
        eveningFirstLesson: LessonData;
        eveningSecondLesson: LessonData;
    };
    psalms: [
        {
            title: string;
            psalms: {
                morning: {
                    ref: string;
                    sections: {
                        verseNumber: number;
                        pointedText: PointedText;
                        toneId: string;
                    }[];
                }[];
                evening: {
                    ref: string;
                    sections: {
                        verseNumber: number;
                        pointedText: PointedText;
                        toneId: string;
                    }[];
                }[];
            };
        }
    ];
    nunc?: PointedText;
    venite?: string;
    invAnts: { [antId: string]: { gabc: string; tone: string } };
    otCanticleOptions: {
        title: string;
        tone: string;
        pointedText?: PointedText;
        gabc?: string;
    }[];
    hymns: { [hymnId: string]: NewHymnData };
    bens: {
        [antId: string]: {
            ant: { gabc: string; tone: string };
            simplifiedAnt: { gabc: string; tone: string };
            ben: PointedText;
            simplifiedBen?: PointedText;
            onlySimplified: boolean;
        };
    };
    mags: {
        [antId: string]: {
            ant: { gabc: string; tone: string };
            simplifiedAnt: { gabc: string; tone: string };
            mag: PointedText;
            simplifiedMag?: PointedText;
            onlySimplified: boolean;
        };
    };
    properVenites: { [antId: string]: string };
}

export const capitalizeGabcFirstWord = (gabc: string) => {
    let isInsideNeume = false;

    let charsSoFar = 0;
    let wordsSoFar = 0;
    let result = "";

    for (let charIndex = 0; charIndex < gabc.length; charIndex++) {
        const char = gabc[charIndex];

        if (char === ")") {
            isInsideNeume = false;
        }

        if (isInsideNeume) {
            result += char;
        } else {
            if (char === " " && charsSoFar > 1) {
                wordsSoFar++;
            }

            if (
                "abcdefghijklmnopqrstuvwxyz".includes(
                    char.toLocaleLowerCase()
                ) &&
                wordsSoFar < 1
            ) {
                charsSoFar++;
                result += char.toLocaleUpperCase();
            } else {
                result += char;
            }
        }

        if (char === "(") {
            isInsideNeume = true;
        }
    }
    return result;
};

export const capitalizeFirstWord = (line: string) => {
    if (line.split(" ")[0].length === 1) {
        return (
            line.split(" ")[0].toLocaleUpperCase() +
            " " +
            line.split(" ")[1].toLocaleUpperCase() +
            " " +
            line.split(" ").slice(2).join(" ")
        );
    } else {
        return (
            line.split(" ")[0].toLocaleUpperCase() +
            " " +
            line.split(" ").slice(1).join(" ")
        );
    }
};

const getVAndRGabc = (
    vAndR: string[],
    isEastertide: boolean,
    isSolemnity: boolean
) => {
    return (
        (isSolemnity ? "(c3) " : "(c4) ") +
        vAndR
            .map((v, vi) => {
                if (isEastertide) {
                    if (v[v.length - 1] === ".") {
                        v = v.slice(0, v.length - 1) + ", al_le_lu_ia.";
                    } else {
                        v += " Al_le_lu_ia.";
                    }
                }

                let capitalized = v;

                if (vi === 0) {
                    capitalized = capitalizeFirstWord(v);
                }

                const syllables = capitalized.replace(/ /g, "_ ").split("_");

                if (isSolemnity) {
                    const firstPart: string[] = [];
                    const secondPart: string[] = [];
                    let isSecondPart =
                        syllables.find((s) => s.trim() === "'") === undefined;

                    for (let s of syllables) {
                        if (s.trim() === "'") {
                            isSecondPart = true;
                        } else {
                            if (isSecondPart) {
                                secondPart.push(s);
                            } else {
                                firstPart.push(s);
                            }
                        }
                    }

                    const result: string[] = [];

                    firstPart.forEach((s) => {
                        if (s.trim()[0] === "/") {
                            result.push(`${s.replace("/", "")}(hi)`);
                        } else {
                            result.push(`${s}(h)`);
                        }
                    });

                    if (firstPart.length > 0) {
                        result.push(" (,) ");
                    }

                    let secondPartNotes = [
                        "h",
                        "f",
                        "e",
                        "fh",
                        "hiHGhih./ghGFE/fg/gf.",
                    ];
                    if (secondPart.length === 4) {
                        secondPartNotes = [
                            "h",
                            "fe",
                            "fh",
                            "hiHGhih./ghGFE/fg/gf.",
                        ];
                    }

                    for (let i = 0; i < secondPart.length; i++) {
                        const fromEnd = secondPart.length - i;
                        const noteIndex = Math.max(
                            0,
                            secondPartNotes.length - fromEnd
                        );

                        result.push(
                            `${secondPart[i]}(${secondPartNotes[noteIndex]})`
                        );
                    }

                    return (
                        result
                            .map((syllable) => {
                                if (vi === 1) {
                                    if (syllable[0] === " ") {
                                        return ` *${syllable
                                            .slice(1)
                                            .replace("(", "*(")}`;
                                    } else {
                                        return `*${syllable.replace(
                                            "(",
                                            "*("
                                        )}`;
                                    }
                                } else {
                                    return syllable;
                                }
                            })
                            .join("") + " (::)"
                    );
                }

                return (
                    syllables
                        .map((s) => {
                            return s.replace("/", "");
                        })
                        .filter((s) => s.trim() !== "'")
                        .map((syllable) => {
                            if (vi === 1) {
                                if (syllable[0] === " ") {
                                    return ` *${syllable.slice(1)}*`;
                                } else {
                                    return `*${syllable}*`;
                                }
                            } else {
                                return syllable;
                            }
                        })
                        .map((syllable, syllableIndex) => {
                            if (syllableIndex === syllables.length - 1) {
                                return `${syllable}(i/jvIHGhih)`;
                            } else {
                                return `${syllable}(j)`;
                            }
                        })
                        .join("") + " (::)"
                );
            })
            .join("")
    );
};

export const getOrdinariateLiturgy = (
    day: CalendarDay,
    hour: "first-evensong" | "mattins" | "second-evensong",
    celebration: Celebration,
    settings: Settings,
    loadableLessons: LoadableDataDependency<OrdinariateLessons>,
    selectedOtCanticle: string,
    onSelectOtCanticle: (newSelection: string) => void,
    officiantType: string,
    onSelectOfficiantType: (newSelection: string) => void,
    antiphonOption: string,
    onSelectAntiphonOption: (newOption: string) => void,
    selectedCommon: string,
    onSelectCommon: (newCommon: string) => void,
    selectedPsalms: string,
    onSelectPsalms: (newPsalms: string) => void,
    isAdvent: boolean,
    isChristmasOctave: boolean,
    isLent: boolean,
    isEastertide: boolean,
    simplifiedHaecDies: string,
    onSelectSimplifiedHaecDies: (newOption: string) => void,
    includeIntroduction: string,
    onSelectIncludeIntroduction: (newOption: string) => void,
    properVenite: string,
    onSelectProperVenite: (newOption: string) => void,
    isBeforeEaster: boolean,
    marianAntiphonLanguage: string,
    onSelectMarianAntiphonLanguage: (newOption: string) => void,
    resurrectionChant: string,
    onSelectResurrectionChant: (newOption: string) => void
) => {
    console.log("celebration", celebration);
    console.log(loadableLessons);

    const isTriduum =
        celebration.label === "maundy-thursday" ||
        celebration.label === "good-friday" ||
        celebration.label === "holy-saturday";

    const psalms =
        loadableLessons.state === "loaded"
            ? hour === "mattins"
                ? loadableLessons.value.psalms.find(
                      (p) => p.title === selectedPsalms || selectedPsalms === ""
                  )?.psalms.morning
                : loadableLessons.value.psalms.find(
                      (p) => p.title === selectedPsalms || selectedPsalms === ""
                  )?.psalms.evening
            : undefined;

    const firstLesson =
        loadableLessons.state === "loaded"
            ? hour === "mattins"
                ? loadableLessons.value.lessons.morningFirstLesson
                : loadableLessons.value.lessons.eveningFirstLesson
            : undefined;

    const secondLesson =
        loadableLessons.state === "loaded"
            ? hour === "mattins"
                ? loadableLessons.value.lessons.morningSecondLesson
                : loadableLessons.value.lessons.eveningSecondLesson
            : undefined;

    const items: LiturgyItem[] = [];

    if (celebration.specialManner) {
        items.push({
            type: "rubric",
            content:
                "On ferias with rank 9 in the table of liturgical days, optional memorials may still be celebrated, but only the Collect of the memorial is used, while the rest of the propers are taken from the feria.",
        });
        items.push({ type: "spacer" });
    } else if (celebration.commons) {
        items.push({
            type: "rubric",
            content: "Supplementary material is taken from the Commons:",
        });

        items.push({
            type: "picker",
            options: celebration.commons,
            selectedOption: selectedCommon,
            onChange: onSelectCommon,
        });

        items.push({ type: "spacer" });
    }

    items.push({
        type: "picker",
        options: ["Include Introduction", "Omit"],
        selectedOption: includeIntroduction,
        onChange: onSelectIncludeIntroduction,
    });
    if (includeIntroduction === "Include Introduction") {
        items.push({ type: "spacer" });
        items.push(...getOpeningSentence(day, "mattins"));

        items.push({
            type: "rubric",
            content:
                "Then the Officiant shall say the following **Exhortation.**",
        });
        items.push({
            type: "text",
            content: `Dearly beloved brethren, the Scripture moveth us, in sundry
            places, to acknowledge and confess our manifold sins and
            wickedness; and that we should not dissemble nor cloak them
            before the face of Almighty God our heavenly Father; but confess
            them with an humble, lowly, penitent, and obedient heart; to the
            end that we may obtain forgiveness of the same, by his infinite
            goodness and mercy. And although we ought, at all times, humbly
            to acknowledge our sins before God; yet ought we most chiefly so
            to do, when we assemble and meet together to render thanks for
            the great benefits that we have received at his hands, to set
            forth his most worthy praise, to hear his most holy Word, and to
            ask those things which are requisite and necessary, as well for
            the body as the soul. Wherefore I pray and beseech you, as many
            as are here present, to accompany me with a pure heart, and
            humble voice, unto the throne of the heavenly grace, saying after me:`,
            dropCap: true,
        });
        items.push({
            type: "rubric",
            content: "All then, kneeling, say:",
        });
        items.push({
            type: "text",
            content: `A**lmighty and most merciful Father; We have erred, and strayed
            from thy ways like lost sheep. We have followed too much the
            devices and desires of our own hearts. We have offended against
            thy holy laws. We have left undone those things which we ought
            to have done; And we have done those things which we ought not
            to have done; And there is no health in us. But thou, O Lord,
            have mercy upon us, miserable offenders. Spare thou them, O God,
            which confess their faults. Restore thou them that are penitent;
            According to thy promises declared unto mankind in Christ Jesu
            our Lord. And grant, O most merciful Father, for his sake; That
            we may hereafter live a godly, righteous, and sober life, To the
            glory of thy holy Name. Amen.**`,
            dropCap: true,
        });

        items.push({
            type: "picker",
            options: ["Lay officiant", "Priest officiant"],
            selectedOption: officiantType,
            onChange: onSelectOfficiantType,
        });

        if (officiantType === "Priest officiant") {
            items.push({
                type: "rubric",
                content:
                    "The Officiant, if he is a Priest, stands and reads the following prayer:",
            });
            items.push({
                type: "text",
                content: `Almighty God, the Father of our Lord Jesus Christ, who
                    desireth not the death of a sinner, but rather that he
                    may turn from his wickedness, and live; and hath given
                    power, and commandment, to his Ministers, to declare and
                    pronounce to his people, being penitent, the Absolution
                    and Remission of their sins: He pardoneth and absolveth
                    all them that truly repent, and unfeignedly believe his
                    holy Gospel. Wherefore let us beseech him to grant us
                    true repentance, and his Holy Spirit, that those things
                    may please him, which we do at this present; and that
                    the rest of our life hereafter may be pure, and holy; so
                    that at the last we may come to his eternal joy; through
                    Jesus Christ our Lord. **Amen.**`,
                dropCap: true,
            });
        } else {
            items.push({
                type: "rubric",
                content:
                    "If the Officiant is not a Priest, the Collect for the Twenty-First Sunday after Trinity is said, all kneeling.",
            });
            items.push({
                type: "text",
                content: `Grant, we beseech thee, merciful Lord, to thy faithful
                    people pardon and peace, that they may be cleansed from
                    all their sins, and serve thee with a quiet mind;
                    through Jesus Christ thy Son our Lord, who liveth and
                    reigneth with thee, in the unity of the Holy Ghost, ever
                    one God, world without end. **Amen.**`,
                dropCap: true,
            });
        }

        items.push({
            type: "rubric",
            content: "Then, all kneeling, say:",
        });
        items.push({
            type: "text",
            content:
                "O**UR Father, who art in heaven, hallowed be thy Name, thy kingdom come, thy will be done, on earth as it is in heaven. Give us this day our daily bread. And forgive us our trespasses, as we forgive those who trespass against us. And lead us not into temptation, but deliver us from evil. Amen.**",
            dropCap: true,
        });
    }
    items.push({ type: "spacer" });

    if (!isTriduum) {
        items.push({
            type: "rubric",
            content: "The Officiant shall say:",
        });

        if (celebration.type === "solemnity") {
            items.push({
                type: "gabc chant",
                gabc: "(c4) O(f) Lord,(gh) (,) o(ixg!hwi)pen(h) thou(h) our(hg) lips.(gh..) (::) *And*(f) *our*(gh) *mouth*(h) (,) *shall*(h) *show*(ixg!hwi) *forth*(h) *thy*(hg) *praise.*(gh..) (::) O(f) God,(gh) (,) make(h) speed(ixg!hwi) to(h) save(hg) us.(gh..) (::) *O*(f) *Lord,*(gh) (,) *make*(h) *haste*(ixg!hwi) *to*(h) *help*(hg) *us.*(gh..) (::) Glo(f)ry(gh) be(h) to(h) the(h) Fa(h)ther,(h) and(h) to(h) the(h) Son,(h) (,) and(h) to(ixg!hwi) the(h) Ho(h)ly(hg) Ghost.(gh..) (::) *As*(f) *it*(gh) *was*(h) *in*(h) *the*(h) *be*(h)*gin*(h)*ning,*(h) (,) *is*(h) *now,*(h) *and*(h) *e*(ixg!hwi)*ver*(h) *shall*(hg) *be,*(gh..) (;) *world*(h) *with*(h)*out*(h) *end.*(ixi) *A*(h)*men.*(g.) (::) Praise(h) ye(ixi) the(h) Lord.(hg..) (::) *The*(h) *Lord's*(h) *Name*(ixi) *be*(h) *prais*(hg)*ed.*(g.) (::)",
                title: "Domine, labia mea",
                dropCap: false,
                tone: "1",
                playButtons: [
                    {
                        gabc: "(c4) O(f) Lord,(gh) (h) (h) (h) (h) (::)",
                        title: "Intone",
                        intoneOnly: true,
                    },
                    {
                        gabc: "(c4) O(f) Lord,(gh) (,) o(ixg!hwi)pen(h) thou(h) our(hg) lips.(gh..) (::)",
                        title: "Full",
                        intoneOnly: false,
                    },
                ],
            });
        } else {
            items.push(...getOpeningResponses(false));
        }

        items.push({ type: "spacer" });

        if (hour === "mattins") {
            // Mattins so add the venite or pascha nostrum

            if (
                celebration.title === "Easter Day" ||
                celebration.title.includes("Octave of Easter")
            ) {
                items.push({
                    type: "rubric",
                    content:
                        "Instead of the Venite the following **Anthems** are said at Morning Prayer of Easter Day and through the Octave:",
                });
                items.push({
                    type: "tone chant",
                    pointedText: getPointedCanticle("pascha nostrum", "2.1"),
                    boldMode: "alternate",
                    tone: "2.1",
                    title: "Pascha Nostrum",
                });
                items.push({ type: "spacer" });
            } else {
                items.push({
                    type: "rubric",
                    content: `The **${
                        day.date === 19 ? "Jubliate Deo" : "Venite"
                    }** is recited, and may be accompanied by the appointed Antiphon.`,
                });

                if (loadableLessons.state === "loaded") {
                    const invAnt =
                        loadableLessons.value.invAnts[celebration.invId ?? ""];

                    let properAnt =
                        loadableLessons.value.invAnts[
                            (celebration.invId ?? "") + " proper"
                        ];
                    properAnt = undefined as unknown as {
                        gabc: string;
                        tone: string;
                    };

                    const properVeniteText =
                        loadableLessons.value.properVenites[
                            (celebration.invId ?? "") + " proper"
                        ];

                    if (properAnt && properVeniteText) {
                        items.push({
                            type: "picker",
                            options: ["Proper chant", "Mode VI"],
                            selectedOption: properVenite,
                            onChange: onSelectProperVenite,
                        });
                    }

                    if (loadableLessons.value.venite && invAnt) {
                        if (
                            properAnt &&
                            properVeniteText &&
                            properVenite === "Proper chant"
                        ) {
                            items.push({
                                type: "gabc chant",
                                title: "Antiphon",
                                gabc: properAnt.gabc,
                                tone: properAnt.tone[1],
                                dropCap: true,
                                playButtons: [
                                    {
                                        gabc: properAnt.gabc,
                                        title: "Intone",
                                        intoneOnly: true,
                                    },
                                    {
                                        gabc: properAnt.gabc,
                                        title: "Play full",
                                        intoneOnly: false,
                                    },
                                ],
                            });

                            items.push({
                                type: "gabc chant",
                                title:
                                    day.date === 19 ? "Jubliate Deo" : "Venite",
                                gabc: properVeniteText,
                                tone: properAnt.tone[1],
                                dropCap: true,
                                playButtons: [
                                    {
                                        gabc: properVeniteText,
                                        title: "Intone",
                                        intoneOnly: true,
                                    },
                                    {
                                        gabc: properVeniteText.split("(::)")[0],
                                        title: "Play line",
                                        intoneOnly: false,
                                    },
                                ],
                            });

                            items.push({
                                type: "gabc chant",
                                title: "Antiphon",
                                gabc: properAnt.gabc,
                                tone: properAnt.tone[1],
                                dropCap: true,
                                playButtons: [
                                    {
                                        gabc: properAnt.gabc,
                                        title: "Intone",
                                        intoneOnly: true,
                                    },
                                    {
                                        gabc: properAnt.gabc,
                                        title: "Play full",
                                        intoneOnly: false,
                                    },
                                ],
                            });
                        } else {
                            items.push({
                                type: "gabc chant",
                                title: "Antiphon",
                                gabc: invAnt.gabc,
                                tone: invAnt.tone,
                                dropCap: true,
                                playButtons: [
                                    {
                                        gabc: invAnt.gabc,
                                        title: "Intone",
                                        intoneOnly: true,
                                    },
                                    {
                                        gabc: invAnt.gabc,
                                        title: "Play full",
                                        intoneOnly: false,
                                    },
                                ],
                            });

                            items.push({
                                type: "gabc chant",
                                title:
                                    day.date === 19 ? "Jubliate Deo" : "Venite",
                                gabc: loadableLessons.value.venite,
                                tone: "VI",
                                dropCap: true,
                                playButtons: [
                                    {
                                        gabc: loadableLessons.value.venite,
                                        title: "Intone",
                                        intoneOnly: true,
                                    },
                                    {
                                        gabc:
                                            loadableLessons.value.venite.split(
                                                "(::)"
                                            )[0] + "(::)",
                                        title: "Play line",
                                        intoneOnly: false,
                                    },
                                ],
                            });

                            items.push({
                                type: "gabc chant",
                                title: "Antiphon",
                                gabc: invAnt.gabc,
                                tone: invAnt.tone,
                                dropCap: true,
                                playButtons: [],
                            });
                        }

                        items.push({ type: "spacer" });
                    }
                } else {
                    items.push({ type: "loading" });
                }
            }
        }
    } else {
        if (
            day.date === 19 &&
            hour === "mattins" &&
            celebration.title !== "Good Friday"
        ) {
            items.push({
                type: "rubric",
                content: `The **Venite** is not sung during the days of the Sacred Paschal Triduum (except as Psalm 95 in the regular course of the Psalms), and the Gloria Patri is omitted throughout the office.`,
            });
        } else if (hour === "mattins") {
            items.push({
                type: "rubric",
                content: `The **Venite** is not sung during the days of the Sacred Paschal Triduum, and the Gloria Patri is omitted throughout the office.`,
            });
        } else {
            items.push({
                type: "rubric",
                content: `During the days of the Sacred Paschal Triduum, the Gloria Patri is omitted throughout the office.`,
            });
        }

        items.push({
            type: "spacer",
        });
    }

    items.push({
        type: "rubric",
        content: `The **Appointed_Psalms** are sung.`,
    });

    if (loadableLessons.state === "loaded") {
        items.push({
            type: "picker",
            options: loadableLessons.value.psalms.map((p) => p.title),
            selectedOption:
                selectedPsalms.length === 0
                    ? loadableLessons.value.psalms[0].title
                    : selectedPsalms,
            onChange: onSelectPsalms,
        });
    }

    if (psalms !== undefined) {
        psalms.forEach((psalm) => {
            items.push({
                type: "ord psalm",
                title: `Psalm ${psalm.ref}`,
                psalmNumber: parseInt(psalm.ref.split(":")[0]),
                sections: psalm.sections,
            });
        });
    } else {
        items.push({ type: "loading" });
    }

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: "The **First_Lesson** is_read.",
    });

    if (firstLesson !== undefined && Array.isArray(firstLesson)) {
        firstLesson.forEach(
            (lessonPart: { title: string; content: any }, index) => {
                items.push({
                    type: "scripture",
                    title: lessonPart.title,
                    content: lessonPart.content,
                    hasBeginneth: index === 0,
                });
            }
        );

        items.push({
            type: "text",
            content: "Here endeth the first lesson.",
        });
    } else {
        items.push({ type: "loading" });
    }

    items.push({ type: "spacer" });

    if (hour === "mattins") {
        // Mattins so add the te deum
        items.push({
            type: "rubric",
            content: `The **First Canticle** is recited.`,
        });
        if (loadableLessons.state === "loaded") {
            const selectedTitle =
                selectedOtCanticle === ""
                    ? loadableLessons.value.otCanticleOptions[0].title
                    : selectedOtCanticle;

            if (loadableLessons.value.otCanticleOptions) {
                items.push({
                    type: "picker",
                    options: loadableLessons.value.otCanticleOptions.map(
                        (opt) => opt.title
                    ),
                    selectedOption: selectedTitle,
                    onChange: onSelectOtCanticle,
                });

                const selectedCanticle =
                    loadableLessons.value.otCanticleOptions.find(
                        (c) => c.title === selectedTitle
                    );

                if (selectedCanticle) {
                    if (selectedCanticle.gabc) {
                        items.push({
                            type: "gabc chant",
                            title: selectedCanticle.title,
                            tone: selectedCanticle.tone,
                            gabc: selectedCanticle.gabc,
                            dropCap: true,
                            playButtons: [
                                {
                                    gabc: selectedCanticle.gabc,
                                    title: "Intone",
                                    intoneOnly: true,
                                },
                            ],
                        });
                    } else if (selectedCanticle.pointedText) {
                        items.push({
                            type: "tone chant",
                            title: selectedCanticle.title,
                            pointedText: selectedCanticle.pointedText,
                            tone: selectedCanticle.tone,
                            boldMode: "alternate",
                        });
                    }
                }

                items.push({ type: "spacer" });
            }
        } else {
            items.push({ type: "loading" });
        }
    }

    const haecDies =
        simplifiedHaecDies === "Simplified"
            ? "(c3) This(d) is(f) the(h) day(h) which(h) £the(i) Lord(i) hath(h) made:(h) (:) we(f) will(h) re(h)£joice(i) and(g) be(g) glad(h) in(f) it.(fe/fvEDe.) (::)"
            : "(c3) <sc>This</sc>(gxfe/gvFE) is(fd) the(fh) day(hhhvFE/fef) *(;) which(e) the(f) Lord(hh/ijhhvFEf.) (,) hath(hfh) made:(hf!gwh!iv./hi/jhh/iih) (:) we(f) will(h) re(h)joice(hg/hi!jvHG/hhg) (,) (ih/ivHF/fhFE/fhhf!hhvGF/heed) (:) and(d/ef~) be(fef) glad(hf) in(hi) it.(iv.hi!jvIH) (,) (jh!jki/hhvFE/f!gwh!iv./hjijHGF!hhf) (::)";

    if (hour !== "mattins") {
        // Evensong so add the hymn and magnificat

        if (isTriduum) {
            items.push({
                type: "rubric",
                content: `No **Office Hymn** is sung during the days of the Sacred Paschal Triduum.`,
            });

            items.push({
                type: "spacer",
            });
        } else if (
            celebration.title === "Easter Day" ||
            celebration.title.includes("Octave of Easter")
        ) {
            items.push({
                type: "rubric",
                content:
                    "Throughout the Octave of Easter, the Hymn and V&R are replaced by the **Hæc Dies**.",
            });

            items.push({
                type: "picker",
                options: ["Full", "Simplified"],
                onChange: onSelectSimplifiedHaecDies,
                selectedOption: simplifiedHaecDies,
            });

            items.push({
                type: "gabc chant",
                gabc: haecDies,
                title: "Hæc Dies",
                dropCap: true,
                tone: "5",
                playButtons: [
                    {
                        gabc: haecDies,
                        title: "Intone",
                        intoneOnly: true,
                    },
                    {
                        gabc: haecDies,
                        title: "Play full",
                        intoneOnly: false,
                    },
                ],
            });

            items.push({
                type: "spacer",
            });
        } else {
            items.push({
                type: "rubric",
                content:
                    "The **Office_Hymn,** together with its Versicle and Response, may be sung.",
            });

            if (loadableLessons.state === "loaded") {
                if (
                    hour === "first-evensong" &&
                    celebration.ep1HymnId &&
                    loadableLessons.value.hymns[celebration.ep1HymnId]
                ) {
                    if (
                        loadableLessons.value.hymns[celebration.ep1HymnId]
                            .ep1Note
                    ) {
                        items.push({
                            type: "rubric",
                            content:
                                loadableLessons.value.hymns[
                                    celebration.ep1HymnId
                                ].ep1Note ?? "",
                        });
                    }

                    items.push({
                        type: "new hymn",
                        hymn: loadableLessons.value.hymns[
                            celebration.ep1HymnId
                        ],
                    });
                }

                if (
                    hour === "second-evensong" &&
                    celebration.ep2HymnId &&
                    loadableLessons.value.hymns[celebration.ep2HymnId]
                ) {
                    if (
                        loadableLessons.value.hymns[celebration.ep2HymnId]
                            .ep2Note
                    ) {
                        items.push({
                            type: "rubric",
                            content:
                                loadableLessons.value.hymns[
                                    celebration.ep2HymnId
                                ].ep2Note ?? "",
                        });
                    }

                    items.push({
                        type: "new hymn",
                        hymn: loadableLessons.value.hymns[
                            celebration.ep2HymnId
                        ],
                    });
                }
            } else {
                items.push({ type: "loading" });
            }

            if (hour === "first-evensong" && celebration.ep1VandR) {
                items.push({
                    type: "gabc chant",
                    title: "Versicle & Response",
                    gabc: getVAndRGabc(
                        celebration.ep1VandR,
                        isEastertide,
                        celebration.type === "solemnity"
                    ),
                    dropCap: true,
                    tone: "",
                    playButtons: [
                        {
                            gabc:
                                celebration.type === "solemnity"
                                    ? "(c3) x(h) x(hi) x(h) x(fe) x(fh) x(hiHGhih./ghGFE/fg/gf.)"
                                    : "(c4) x(ji/jvIHGhih) (:)",
                            title: "Play",
                            intoneOnly: false,
                        },
                    ],
                });
            }

            if (hour === "second-evensong" && celebration.ep2VandR) {
                items.push({
                    type: "gabc chant",
                    title: "Versicle & Response",
                    gabc: getVAndRGabc(
                        celebration.ep2VandR,
                        isEastertide,
                        celebration.type === "solemnity"
                    ),
                    dropCap: true,
                    tone: "",
                    playButtons: [
                        {
                            gabc:
                                celebration.type === "solemnity"
                                    ? "(c3) x(h) x(hi) x(h) x(fe) x(fh) x(hiHGhih./ghGFE/fg/gf.)"
                                    : "(c4) x(ji/jvIHGhih) (:)",
                            title: "Play",
                            intoneOnly: false,
                        },
                    ],
                });
            }

            items.push({ type: "spacer" });
        }

        items.push({
            type: "rubric",
            content:
                "The **Magnificat** is recited, and may be accompanied by the appointed Antiphon.",
        });

        if (loadableLessons.state === "loaded") {
            const mag =
                loadableLessons.value.mags[
                    (hour === "first-evensong"
                        ? celebration.ep1MagId
                        : celebration.ep2MagId) ?? ""
                ];

            if (mag) {
                if (mag.onlySimplified) {
                    items.push({
                        type: "rubric",
                        content:
                            "No full setting of this Antiphon_is_available.",
                    });
                } else {
                    items.push({
                        type: "picker",
                        options: ["Full", "Simplified"],
                        onChange: onSelectAntiphonOption,
                        selectedOption: antiphonOption,
                    });
                }

                const magAnt =
                    antiphonOption === "Full" && !mag.onlySimplified
                        ? mag.ant
                        : mag.simplifiedAnt;

                items.push({
                    type: "gabc chant",
                    title: "Antiphon",
                    gabc: magAnt.gabc,
                    tone: magAnt.tone,
                    dropCap: true,
                    playButtons: [
                        {
                            gabc: magAnt.gabc,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: magAnt.gabc,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({
                    type: "tone chant",
                    pointedText:
                        (antiphonOption === "Simplified" ||
                            mag.onlySimplified) &&
                        mag.simplifiedMag
                            ? mag.simplifiedMag
                            : mag.mag,
                    tone:
                        antiphonOption === "Full"
                            ? mag.ant.tone
                            : mag.ant.tone.replace("trans", ""),
                    boldMode: "alternate",
                    title: "Magnificat",
                });

                items.push({
                    type: "gabc chant",
                    title: "Antiphon",
                    gabc: magAnt.gabc,
                    tone: magAnt.tone,
                    dropCap: true,
                    playButtons: [
                        {
                            gabc: magAnt.gabc,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: magAnt.gabc,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({ type: "spacer" });
            }
        } else {
            items.push({ type: "loading" });
        }
    }

    items.push({
        type: "rubric",
        content: "The **Second_Lesson** is_read.",
    });

    if (celebration.label === "easter-day" && hour === "second-evensong") {
        items.push({
            type: "rubric",
            content:
                "While normatively the lessons are read, on Easter Day the Second Lesson could be sung to the very solemn tone of the Resurrection Gospel.",
        });

        items.push({
            type: "picker",
            options: ["Text", "Very solemn tone"],
            onChange: onSelectResurrectionChant,
            selectedOption: resurrectionChant,
        });
    }

    if (secondLesson !== undefined && Array.isArray(secondLesson)) {
        if (resurrectionChant === "Very solemn tone") {
            items.push({
                type: "gabc chant",
                title: "John 20:1-23",
                gabc: RESURRECTION_GOSPEL,
                tone: "",
                dropCap: true,
                playButtons: [
                    {
                        gabc: "(c3) a(jijIHGF) b(f) c(e) d(fg) e(h) (,) f(f) g(h) h(hij) i(i) j(hvGFg) k(e) l(f) (:)",
                        title: "Play",
                        intoneOnly: false,
                    },
                ],
            });
        } else {
            secondLesson.forEach(
                (lessonPart: { title: string; content: any }, index) => {
                    items.push({
                        type: "scripture",
                        title: lessonPart.title,
                        content: lessonPart.content,
                        hasBeginneth: index === 0,
                    });
                }
            );
        }

        items.push({
            type: "text",
            content: "Here endeth the second lesson.",
        });
    } else {
        items.push({ type: "loading" });
    }

    items.push({ type: "spacer" });

    if (hour === "mattins") {
        // Mattins so add the hymn and benedictus

        if (isTriduum) {
            items.push({
                type: "rubric",
                content: `No **Office Hymn** is sung during the days of the Sacred Paschal Triduum.`,
            });

            items.push({
                type: "spacer",
            });
        } else if (
            celebration.title === "Easter Day" ||
            celebration.title.includes("Octave of Easter")
        ) {
            items.push({
                type: "rubric",
                content:
                    "Throughout the Octave of Easter, the Hymn and V&R are replaced by the **Hæc Dies**.",
            });

            items.push({
                type: "picker",
                options: ["Full", "Simplified"],
                onChange: onSelectSimplifiedHaecDies,
                selectedOption: simplifiedHaecDies,
            });

            items.push({
                type: "gabc chant",
                gabc: haecDies,
                title: "Hæc Dies",
                dropCap: true,
                tone: "5",
                playButtons: [
                    {
                        gabc: haecDies,
                        title: "Intone",
                        intoneOnly: true,
                    },
                    {
                        gabc: haecDies,
                        title: "Play full",
                        intoneOnly: false,
                    },
                ],
            });

            items.push({
                type: "spacer",
            });
        } else {
            items.push({
                type: "rubric",
                content:
                    "The **Office_Hymn,** together with its Versicle and Response, may be sung.",
            });

            if (loadableLessons.state === "loaded") {
                if (
                    celebration.mpHymnId &&
                    loadableLessons.value.hymns[celebration.mpHymnId]
                ) {
                    if (
                        loadableLessons.value.hymns[celebration.mpHymnId].mpNote
                    ) {
                        items.push({
                            type: "rubric",
                            content:
                                loadableLessons.value.hymns[
                                    celebration.mpHymnId
                                ].mpNote ?? "",
                        });
                    }

                    items.push({
                        type: "new hymn",
                        hymn: loadableLessons.value.hymns[celebration.mpHymnId],
                    });
                }
            } else {
                items.push({ type: "loading" });
            }

            if (celebration.mpVandR) {
                items.push({
                    type: "gabc chant",
                    title: "Versicle & Response",
                    gabc: getVAndRGabc(
                        celebration.mpVandR,
                        isEastertide,
                        celebration.type === "solemnity"
                    ),
                    dropCap: true,
                    tone: "",
                    playButtons: [
                        {
                            gabc:
                                celebration.type === "solemnity"
                                    ? "(c3) x(h) x(hi) x(h) x(fe) x(fh) x(hiHGhih./ghGFE/fg/gf.)"
                                    : "(c4) x(ji/jvIHGhih) (:)",
                            title: "Play",
                            intoneOnly: false,
                        },
                    ],
                });
            }

            items.push({ type: "spacer" });
        }

        items.push({
            type: "rubric",
            content:
                "The **Benedictus** is recited, and may be accompanied by the appointed Antiphon.",
        });

        if (loadableLessons.state === "loaded") {
            const ben = loadableLessons.value.bens[celebration.mpBenId ?? ""];

            if (ben) {
                if (ben.onlySimplified) {
                    items.push({
                        type: "rubric",
                        content:
                            "No full setting of this Antiphon_is_available.",
                    });
                } else {
                    items.push({
                        type: "picker",
                        options: ["Full", "Simplified"],
                        onChange: onSelectAntiphonOption,
                        selectedOption: antiphonOption,
                    });
                }

                const benAnt =
                    antiphonOption === "Full" && !ben.onlySimplified
                        ? ben.ant
                        : ben.simplifiedAnt;

                items.push({
                    type: "gabc chant",
                    title: "Antiphon",
                    gabc: benAnt.gabc,
                    tone: benAnt.tone,
                    dropCap: true,
                    playButtons: [
                        {
                            gabc: benAnt.gabc,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: benAnt.gabc,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({
                    type: "tone chant",
                    pointedText:
                        (antiphonOption === "Simplified" ||
                            ben.onlySimplified) &&
                        ben.simplifiedBen
                            ? ben.simplifiedBen
                            : ben.ben,
                    tone:
                        antiphonOption === "Full"
                            ? ben.ant.tone
                            : ben.ant.tone.replace("trans", ""),
                    boldMode: "alternate",
                    title: "Benedictus",
                });

                items.push({
                    type: "gabc chant",
                    title: "Antiphon",
                    gabc: benAnt.gabc,
                    tone: benAnt.tone,
                    dropCap: true,
                    playButtons: [
                        {
                            gabc: benAnt.gabc,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: benAnt.gabc,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({ type: "spacer" });
            }
        } else {
            items.push({ type: "loading" });
        }
    }

    if (hour !== "mattins") {
        // Evensong so add the nunc

        items.push({
            type: "rubric",
            content: "The **Nunc Dimittis** is recited.",
        });

        if (loadableLessons.state === "loaded") {
            if (loadableLessons.value.nunc) {
                items.push({
                    type: "tone chant",
                    pointedText: loadableLessons.value.nunc,
                    tone: "3a.2",
                    boldMode: "alternate",
                    title: "Nunc Dimittis",
                });

                items.push({ type: "spacer" });
            }
        } else {
            items.push({ type: "loading" });
        }
    }

    if (celebration.athanasianCreed && hour === "mattins") {
        items.push({
            type: "rubric",
            content: "On this day, the **Athanasian Creed** is said.",
        });
        items.push({
            type: "tone chant",
            pointedText: getPointedCanticle("quicunque vult", "4.4"),
            boldMode: "alternate",
            tone: "4.4",
            title: "Quicunque Vult",
        });
    } else {
        items.push({
            type: "rubric",
            content:
                "The **Apostle's Creed** shall be said by the Officiant and People standing.",
        });
        items.push({
            type: "text",
            content: `I **BELIEVE in God the Father Almighty, Maker of heaven and
                earth; and in Jesus Christ, his only Son our Lord; Who was
                conceived by the Holy Ghost; Born of the Virgin Mary; Suffered
                under Pontius Pilate; Was crucified, dead, and buried; He
                descended into hell; The third day he rose again from the dead;
                He ascended into heaven, And sitteth on the right hand of God
                the Father Almighty; From thence he shall come to judge the
                quick and the dead. I believe in the Holy Ghost; The holy
                Catholic Church; The Communion of Saints; The Forgiveness of
                sins; The Resurrection of the body; And the life everlasting.
                Amen.**`,
            dropCap: true,
        });
    }

    items.push({
        type: "rubric",
        content: "And afterward these Prayers are said, all devoutly kneeling.",
    });
    items.push({
        type: "picker",
        options: ["Lay officiant", "Priest officiant"],
        selectedOption: officiantType,
        onChange: onSelectOfficiantType,
    });
    items.push(...getSalutation(settings));

    items.push({
        type: "minimal gabc chant",
        gabc: "(f3) Lord(h) have(h) mer(h)cy(h) up(h)on(hg) us.(h) (::) *Christ*(h) *have*(h) *mer*(h)*cy*(h) *up*(h)*on*(hg) *us.*(h) (::) Lord(f) have(f) mer(f)cy(f) up(f)on(fe) us.(f) (::)",
        tone: "",
        dropCap: false,
    });

    items.push({
        type: "rubric",
        content:
            "Then the Officiant and People shall say the **Lord's Prayer.**",
    });
    items.push({
        type: "text",
        content:
            "O**UR Father, who art in heaven, hallowed be thy Name, thy kingdom come, thy will be done, on earth as it is in heaven. Give us this day our daily bread. And forgive us our trespasses, as we forgive those who trespass against us. And lead us not into temptation, but deliver us from evil. Amen.**",
        dropCap: true,
    });

    items.push({
        type: "rubric",
        content: "Then shall follow these **Versicles_and_Responses.**",
    });
    items.push(...getSuffrages(settings));
    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: `The **Collect of the Day** is_said.`,
    });
    (celebration.collect ?? "").split("//").forEach((collect, ci) => {
        if (ci > 0) {
            items.push({
                type: "rubric",
                content: `This Collect is also said.`,
            });
        }

        items.push({
            type: "text",
            content:
                (collect.length > 0 ? collect : undefined)?.replace(
                    ". Amen.",
                    ". **Amen.**"
                ) ?? "Not found",
            dropCap: true,
        });
    });

    if (isLent && celebration.title !== "Ash Wednesday") {
        items.push({
            type: "rubric",
            content: `Throughout Lent, the **Collect for Ash Wednesday** is_said.`,
        });
        items.push({
            type: "text",
            content:
                "Almighty and everlasting God, who hatest nothing that thou hast made and dost forgive the sins of all those who are penitent: create and make in us new and contrite hearts, that we, worthily lamenting our sins and acknowledging our wretchedness, may obtain of thee, the God of all mercy, perfect remission and forgiveness; through Jesus Christ thy Son our Lord, who liveth and reigneth with thee, in the unity of the Holy Spirit, ever one God, world without end. **Amen.**",
            dropCap: true,
        });
    }

    if (
        isAdvent &&
        celebration.collect !==
            "Almighty God, give us grace that we may cast away the works of darkness, and put upon us the armour of light, now in the time of this mortal life, in which thy Son Jesus Christ came to visit us in great humility; that in the last day, when he shall come again in his glorious majesty, to judge both the quick and the dead; we may rise to the life immortal; through the same Jesus Christ thy Son our Lord, who liveth and reigneth with thee, in the unity of the Holy Spirit, ever one God, world without end. Amen." &&
        celebration.label !== "christmas-day"
    ) {
        items.push({
            type: "rubric",
            content: `Throughout Advent, the **Collect for Advent_I** is_said.`,
        });
        items.push({
            type: "text",
            content:
                "Almighty God, give us grace that we may cast away the works of darkness, and put upon us the armour of light, now in the time of this mortal life, in which thy Son Jesus Christ came to visit us in great humility; that in the last day, when he shall come again in his glorious majesty, to judge both the quick and the dead; we may rise to the life immortal; through the same Jesus Christ thy Son our Lord, who liveth and reigneth with thee, in the unity of the Holy Spirit, ever one God, world without end. **Amen.**",
            dropCap: true,
        });
    }

    if (
        isChristmasOctave &&
        celebration.collect !==
            "Almighty God, who hast given us thy Only Begotten Son to take our nature upon him, and as at this time to be born of a pure Virgin: grant that we being regenerate, and made thy children by adoption and grace, may daily be renewed by thy Holy Spirit; through the same Jesus Christ thy Son our Lord, who liveth and reigneth with thee, in the unity of the Holy Spirit, ever one God, world without end. Amen."
    ) {
        items.push({
            type: "rubric",
            content: `During the Octave of Christmas, the **Collect for Christmas_Day** is_said daily.`,
        });
        items.push({
            type: "text",
            content:
                "Almighty God, who hast given us thy Only Begotten Son to take our nature upon him, and as at this time to be born of a pure Virgin: grant that we being regenerate, and made thy children by adoption and grace, may daily be renewed by thy Holy Spirit; through the same Jesus Christ thy Son our Lord, who liveth and reigneth with thee, in the unity of the Holy Spirit, ever one God, world without end. **Amen.**",
            dropCap: true,
        });
    }

    if (hour === "mattins") {
        items.push({
            type: "rubric",
            content: "The **Collect_for_Peace** is said.",
        });
        items.push({
            type: "text",
            content: `O GOD, who art the author of peace and lover of concord, in knowledge of whom standeth our eternal life, whose service is perfect freedom: defend us thy humble servants in all assaults of our enemies; that we, surely trusting in thy defence, may not fear the power of any adversaries; through the might of Jesus Christ our Lord. **Amen.**`,
            dropCap: true,
        });

        items.push({
            type: "rubric",
            content: "The **Collect_for_Grace** is said.",
        });
        items.push({
            type: "text",
            content: `O LORD, our heavenly Father, Almighty and everlasting God, who
                hast safely brought us to the beginning of this day; Defend us
                in the same with thy mighty power; and grant that this day we
                fall into no sin, neither run into any kind of danger; but that
                all our doings may be ordered by thy governance, to do always
                that is righteous in thy sight; through Jesus Christ our
                Lord. **Amen.**`,
            dropCap: true,
        });
    } else {
        items.push({
            type: "rubric",
            content: "The **Collect_for_Peace** is said.",
        });
        items.push({
            type: "text",
            content: `O GOD, from whom all holy desires, all good counsels, and all
                just works do proceed; Give unto thy servants that peace which
                the world cannot give; that both our hearts may be set to obey
                thy commandments, and also that by thee, we, being defended from the
                fear of our enemies, may pass our time in rest and quietness;
                through the merits of Jesus Christ our Saviour. **Amen.**`,
            dropCap: true,
        });

        items.push({
            type: "rubric",
            content: "The **Collect_for_Aid_Against_all_Perils** is said.",
        });
        items.push({
            type: "text",
            content: `LIGHTEN our darkness, we beseech thee, O Lord; and by thy great
                mercy defend us from all perils and dangers of this night; for
                the love of thy only Son, our Saviour, Jesus Christ. **Amen.**`,
            dropCap: true,
        });
    }

    items.push({
        type: "rubric",
        content:
            "In quires and places where they sing, an Anthem may follow. Then these five prayers may be read, or at least the Prayer of Saint Chrysostom and the Grace.",
    });

    const cfeThursday =
        "(c4) Christ(f/fgf) $(,) be(f)came(fg) for(f) us(ghF'ED.g_e/fgf.) (;) o(f)be(hf/gh!jj/kjk)di(jij)ent(h_g) (,) e(h!jjh)ven(f.) un(ixfiH'Gh)to(f) death.(ff_g_f_) (::)";
    const cfeFriday =
        "(c4) Christ(f/fgf) $(,) be(f)came(fg) for(f) us(ghF'ED.g_e/fgf.) (;) o(f)be(hf/gh!jj/kjk)di(jij)ent(h_g) (,) e(h!jjh)ven(f.) un(ixfiH'Gh)to(f) death,(ff_g_f_) (:) death(ixf!hhig) on(ixgjj.h!iw!jvIG'hw!ivHG) a(f) cross.(fv.fff_c) (,) (fg!hvGF.e!g'h//fhg/hggf.) (::)";
    const pqed =
        "(c3) Where(d)fore(d) God(fe) hath(eh) high(h)ly(h) ex(h)al(hihhe./h_i_h_/ihhe.,hi!kv//lvKI'jvHF.,h_g/ijh'___ jvIH)ted(jvIH) him,(hvGF.) (:) and(f) gi(f!h'i)ven(i) him(ih/jkihhf.) (,) a(hf) name(hhhh.f!gwh!iv.hi/jhh/iih.) (;) which(d) is(d) a(d)bove(dfE'D/ef) ev(fhG'F)ery(hhh) name.(hhf.) (,) (gxg_fgvED.fgED.fehv.hhhff//dfe/feed.) (::)";

    if (isTriduum) {
        items.push({ type: "rubric", content: "This Anthem is suggested." });

        if (celebration.label === "maundy-thursday") {
            items.push({
                type: "gabc chant",
                gabc: cfeThursday,
                title: "Chritus factus est",
                dropCap: true,
                tone: "5",
                playButtons: [
                    {
                        gabc: cfeThursday,
                        title: "Intone",
                        intoneOnly: true,
                    },
                    {
                        gabc: cfeThursday,
                        title: "Play full",
                        intoneOnly: false,
                    },
                ],
            });
        }

        if (celebration.label === "good-friday") {
            items.push({
                type: "gabc chant",
                gabc: cfeFriday,
                title: "Chritus factus est",
                dropCap: true,
                tone: "5",
                playButtons: [
                    {
                        gabc: cfeFriday,
                        title: "Intone",
                        intoneOnly: true,
                    },
                    {
                        gabc: cfeFriday,
                        title: "Play full",
                        intoneOnly: false,
                    },
                ],
            });
        }

        if (celebration.label === "holy-saturday") {
            items.push({
                type: "gabc chant",
                gabc: cfeFriday,
                title: "Chritus factus est",
                dropCap: true,
                tone: "5",
                playButtons: [
                    {
                        gabc: cfeFriday,
                        title: "Intone",
                        intoneOnly: true,
                    },
                    {
                        gabc: cfeFriday,
                        title: "Play full",
                        intoneOnly: false,
                    },
                ],
            });
            items.push({
                type: "gabc chant",
                gabc: pqed,
                title: "Propter quad et Deus",
                dropCap: true,
                tone: "5",
                playButtons: [
                    {
                        gabc: pqed,
                        title: "Intone",
                        intoneOnly: true,
                    },
                    {
                        gabc: pqed,
                        title: "Play full",
                        intoneOnly: false,
                    },
                ],
            });
        }
    }

    items.push({ type: "rubric", content: "A Prayer for the King's Majesty" });
    items.push({
        type: "text",
        content:
            "O LORD, our heavenly Father, high and mighty, King of kings, Lord of lords, the only Ruler of princes, who dost from thy throne behold all the dwellers upon earth: Most heartily we beseech thee with thy favour to behold our most gracious Sovereign Lord, King Charles; and so replenish him with the grace of thy Holy Spirit, that he may alway incline to thy will, and walk in thy way. Endue him plenteously with heavenly gifts; grant him in health and wealth long to live; strengthen him that he may vanquish and overcome all his enemies; and finally after this life he may attain everlasting joy and felicity; through Jesus Christ our Lord. **Amen.**",
        dropCap: true,
    });

    items.push({ type: "rubric", content: "A Prayer for the Royal Family" });
    items.push({
        type: "text",
        content:
            "ALMIGHTY God, the fountain of all goodness, we humbly beseech thee to bless Queen Camilla, William Prince of Wales, the Princess of Wales, and all the Royal Family: Endue them with thy Holy Spirit; enrich them with thy heavenly grace; prosper them with all happiness; and bring them to thine everlasting kingdom; through Jesus Christ our Lord. **Amen.**",
        dropCap: true,
    });
    items.push({ type: "rubric", content: "Or in the Commonwealth:" });
    items.push({
        type: "text",
        content:
            "ALMIGHTY God, the fountain of all goodness, we humbly beseech thee to bless our Sovereign Lord, King Charles, the Parliaments of the Commonwealth, and all who are set in authority under him; that they may order all things in wisdom, righteousness, and peace, to the honour of thy holy Name, and the good of thy Church and people; through Jesus Christ our Lord. **Amen.**",
        dropCap: true,
    });

    items.push({
        type: "rubric",
        content: "A Prayer for the Clergy and People",
    });
    items.push({
        type: "text",
        content:
            "ALMIGHTY and everlasting God, who alone workest great marvels: Send down upon our Bishops, and Curates, and all Congregations committed to their charge, the healthful Spirit of thy grace; and that they may truly please thee, pour upon them the continual dew of thy blessing. Grant this, O Lord, for the honour of our Advocate and Mediator, Jesus Christ. **Amen.**",
        dropCap: true,
    });

    // if (settings.oneDayAheadPsalter === "true") {
    //     items.push({
    //         type: "rubric",
    //         content: "A Prayer for the Sick",
    //     });
    //     items.push({
    //         type: "text",
    //         content:
    //             "O FATHER of all mercies and God of all comfort, our only help in time of need: We humbly beseech thee to behold, visit, and relieve thy sick servant N., for whom our prayers are desired. Look upon her with the eyes of thy mercy, comfort her with a sense of thy goodness, preserve her from the temptations of the enemy, and give her patience under her affliction. In thy good time, restore her to health, and enable her to lead the rest of her life in thy fear and to thy glory, and grant that finally she may dwell with thee in life everlasting, through Jesus Christ our Lord. **Amen.**",
    //         dropCap: true,
    //     });
    // }

    items.push({
        type: "rubric",
        content: "A Prayer of Saint Chrysostom",
    });
    items.push({
        type: "text",
        content:
            "ALMIGHTY God, who hast given us grace at this time with one accord to make our common supplications unto thee; and dost promise that when two or three are gathered together in thy Name thou wilt grant their requests: Fulfil now, O Lord, the desires and petitions of thy servants, as may be most expedient for them; granting us in this world knowledge of thy truth, and in the world to come life everlasting. **Amen.**",
        dropCap: true,
    });

    items.push({
        type: "rubric",
        content: "The Grace",
    });
    items.push({
        type: "text",
        content:
            "THE grace of our Lord Jesus Christ, and the love of God, and the fellowship of the Holy Ghost, be with us all evermore. **Amen.**",
        dropCap: true,
    });

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: `Here ends the Order of ${
            hour === "mattins" ? "Morning" : "Evening"
        } Prayer.`,
    });

    items.push({ type: "spacer" });

    const aveReginaCaelorumLatin =
        "(c4) A(f)ve(e') Re(d)gí(c')na(d) cæ(f')ló(g)rum,(f.) $(;) A(h')ve(j) Dó(ixi')mi(g)na(h') An(g)ge(f')ló(h)rum:(g.) (;) Sal(f)ve(e') ra(d)dix,(c') sal(d)ve(f') por(g)ta,(f'_) (;) Ex(h) qua(g') mun(ixi)do(h') lux(g) est(d') or(g)ta:(f.) (:) Gau(f)de(g') Vir(h)go(h') glo(g)ri(h')ó(ixi)sa,(h'_) (;) Su(j)per(ixi') o(h)mnes(g') spe(f)ci(d')ó(g)sa:(f.) (:) Va(ixi)le,(h') o(g) val(ixi)de(h') de(g)có(f!gwh)ra,(h.) (;) Et(j) pro(ixi) no(ixijk)bis(h'_) Chri(h)stum(g') ex(f)ó(gg)ra.(f.) (::)";
    const aveReginaCaelorumEnglish =
        "(c4) Queen(f) of(e') the(d) hea(c')vens(d) we(f') hail(g) thee:(f.) (;) Hail(h') thee(j) La(ixi')dy(g) of(h') all(g) the(f') An(h)gels:(g.) (;) Thou(f) the(e') dawn,(d) the(c') door(d) of(f') morn(g)ing,(f'_) (;) whence(h) the(g') world's(ixi) true(h') light(g) is(d') ri(g)sen:(f.) (:) Joy(f) to(g') thee(h) O(h') Vir(g)gin(h') glo(ixi)rious,(h'_) (;) Beau(j)ti(ixi')ful(h) bey(g')ond(f) all(d') o(g)ther:(f.) (:) Hail(ixi) and(h') fare(g)well(ixi) O(h') most(g) gra(f!gwh)cious,(h.) (;) In(j)ter(ixi)cede(ixij) for(k) us(hh) al(h)way(g') to(f) Je(gg)sus.(f.) (::)";
    const reginaCaeliLatin =
        "(c4) RE(f)gi(g)na(f') cæ(g)li(h.) $(,) læ(ixi)tá(h)re,(g'_) al(ixi)le(h')lú(g)ia:(f.) (;) Qui(f)a(j'_) quem(j) me(k')ru(j)í(ixi')sti(h) por(f')tá(g)re,(h'_) al(ixi)le(h')lú(g)ia:(f.) (:) Re(j)sur(j')ré(k)xit,(j'_) sic(j)ut(f') di(g)xit,(f'_) al(g)le(h')lú(ixi)ia:(j.) (;) O(j)ra(f) pro(g') no(ixi)bis(h') De(g)um,(f.) al(e)le(g)lú(gg)ia.(f.) (::)";
    const reginaCaeliEnglish =
        "(c4) O(f) Queen(g) of(f') hea(g)ven(h.) *(,) be(ixi) joy(h)ful,(g) al(ixi)le(h')lu(g)ia:(f.) (;) Be(f)cause(j'_) he(j) whom(k') so(j) meet(ixi')ly(h) thou(f') bear(g)est,(h'_) al(ixi)le(h')lu(g)ia:(f.) (:) Hath(j) a(j')ri(k)sen(j'_) as(j) he(f') pro(g)mis'd,(f'_) al(g)le(h')lu(ixi)ia:(j.) (;) Pray(j) for(f) us(g') to(ixi) the(h') Fa(g)ther,(f.) al(e)le(g)lu(gg)ia.(f.) (::)";
    const salveReginaLatin =
        "(c4) SAl(c)ve,(e) Re(g')gí(h)na,(g.) $(,) ma(h)ter(j') mi(i)se(h)ri(g)cór(h')di(g)ae:(g.) (;) Vi(j)ta,(g') dul(h)cé(ff)do,(d.) (;) et(e) spes(f) no(g')stra,(e) sal(ed)ve.(c.) (::) Ad(g) te(h) cla(i')má(j)mus,(g.) (;) éx(h)su(i)les,(j) fí(i')li(h)i(g') He(h)vae.(g.) (::) Ad(j) te(g') sus(h)pi(f')rá(d)mus,(e'_) (;) ge(e)mén(g')tes(h) et(j') flen(h)tes(g'_) (;) in(h) hac(g') la(f)cri(e')má(d)rum(e') val(d)le.(c.) (::) E(g)ia(h') er(i)go,(j'_) Ad(g)vo(h')cá(j)ta(i') no(h)stra,(g.) (;) il(j)los(g') tu(h)os(f'_) mi(d)se(e')ri(f)cór(g')des(f) ó(h')cu(g)los(g'_) (,) ad(f) nos(e') con(d)vér(ed)te.(c.) (::) Et(g) Je(hi)sum,(jj) be(i)ne(g')dí(h)ctum(g') fru(g)ctum(h') ven(j)tris(i') tu(h)i,(g.) (;) no(c)bis(g'_) post(h) hoc(j') ex(i)sí(h')li(g)um(e'_) os(f)tén(ed)de.(c.) (::) O(e!fwg) cle(ee)mens:(c.) (::) O(g!hwi/ji) pi(hg)a:(g.) (::) O(jjj//ghf'fd) dul(ef)cis(g'_) (;) Vir(c)go(f') Ma(e)rí(dc)a.(c.) (::)";
    const salveReginaEnglish =
        "(c4) Ma(c)ry(e) we(g') hail(h) thee(g.) $(,) mo(h)ther(j') and(i) queen(h) com(g)pas(h')sion(g)ate:(g.) (;) Ma(j)ry,(g') our(h) com(ff)fort,(d.) (,) life(e) and(f) hope(g') we(e) hail(ed) thee.(c.) (::) To(g) thee(h) we(i') ex(j)iles,(g.) (;) chil(h)dren(i) of(j) Eve,(i') lift(h) our(g') cry(h)ing.(g.) (::) To(j) thee(g') we(h) are(f') sigh(d)ing,(e'_) (;) as(e) mourn(g')ful(h) and(j') weep(h)ing(g'_) (,) we(h) pass(g') through(f) this(e') vale(d) of(e') sor(d)row.(c.) (::) Turn(g) thou,(h') there(i)fore,(j'_) (,) O(g) our(h') in(j)ter(i')ces(h)sor,(g.) (;) those(j) thine(g') eyes(h) of(f'_) pi(d)ty(e') and(f) lo(g')ving(f) kind(hg~)ness(g'_) u(f)pon(e') us(d) sin(ed)ners.(c.) (::) Here(g)af(hi)ter,(j) (,) when(i) our(g') earth(h)ly(g') ex(g)ile(h') shall(j) be(i') en(h)ded,(g.) (;) show(c) us(g'_) Je(hi)sus(jj) (,) the(i) bless(h')ed(g) fruit(e'_) of(f) thy(ed) womb.(c.) (::) O(e!fwg) gen(ee)tle:(c.) (::) O(g!hwi/ji) ten(hg)der:(g.) (::) O(jjj//ghf'fd) gra(ef)cious(g'_) (,) Vir(c)gin(fe~) Ma(dc)ry.(c.) (::)";
    const almaEngilsh =
        "(c4) Gra(c!ef/gh)cious(g.) Mo(g)ther(g) of(g) our(h) Re(i)dee(j)mer,(g.) (;) for(e) e(e)ver(e) a(f)bi(e)ding,(d) Hea(e)ven's(f) gate(h)way,(g.) (,) a(h) star(j) of(i) o(h)cean,(g.) (;) O(e) suc(f)cour(e) th(d) peo(e)ple,(g.) (;) who(e) though(f) fall(g)ing(h) strive(j) to(i) rise(k') a(j)gain.(j.) (:) Thou(j) mai(i)den(j) who(j) bear(k)est(g) (,) thy(j) ho(i)ly(h) cre(g)a(f)tor,(e.) (,) to(e) the(e) won(h)der(g) of(f) all(e) na(de)ture;(fe) (:) E(j)ver(i) vir(h)gin(g) (,) af(h)ter(g) as(e) be(f)fore(g) thou(h) re(h)ceiv(j)edst(h) that(g) A(f)ve(e) (,) from(f) the(e) mouth(g) of(g) Ga(h)briel;(g.) (;) have(j) com(i)pas(j)sion(hg) on(h) us(f) sin(ed)ners.(c.) (::)";
    const almaLatin =
        "(c4) Al(c!ef/gh)ma(g.) *() Red(g)em(g')ptó(h)ris(i') Ma(j)ter,(g.) (;) quae(e) pér(e')vi(e)a(f') cae(e)li(d') por(e)ta(f') ma(h)nes,(g.) (;) Et(h) stel(j)la(i') ma(h)ris,(g.) (;) suc(e)cúr(f')re(e) ca(d')dén(e)ti(g.) (;) súr(e)ge(f)re(g') qui(h) cu(j')rat(i) pó(k')pu(j)lo :(j.) (:) Tu(j) quae(i') ge(j)nu(j')í(k)sti,(g'_) (;) na(j)tú(i')ra(h) mi(g')rán(f)te,(e.) (;) tu(e)um(e) san(h')ctum(g) Ge(f')ni(e)tó(de)rem :(fe..) (:) Vir(j)go(i') pri(h)us(g'_) ac(h) po(g)sté(e')ri(f)us,(g.) (;) Ga(h)bri(h)é(j')lis(h) ab(g') o(f)re(e'_) (;) su(f)mens(e') il(g)lud(g') A(h)ve,(g.) (;) pec(j)ca(i')tó(j)rum(hg) mi(h)se(f)ré(ed)re.(c.) (::)";

    if (!isTriduum) {
        items.push({
            type: "rubric",
            content:
                "The Marian Anthem appointed for the season may be recited.",
        });

        items.push({
            type: "picker",
            options: ["Latin", "English"],
            selectedOption: marianAntiphonLanguage,
            onChange: onSelectMarianAntiphonLanguage,
        });

        if (marianAntiphonLanguage === "English") {
            if (
                isAdvent ||
                day.month === 1 ||
                (day.month === 2 && day.date <= 2)
            ) {
                items.push({
                    type: "gabc chant",
                    gabc: almaEngilsh,
                    title: "Alma Redemptoris Mater",
                    dropCap: true,
                    tone: "5",
                    playButtons: [
                        {
                            gabc: almaEngilsh,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: almaEngilsh,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                if (isAdvent) {
                    items.push({
                        type: "text",
                        content: `The Angel of the Lord declared unto Mary:

                        **And she conceived by the Holy Ghost.**`,
                    });
                    items.push({
                        type: "text",
                        content: `Let us pray.

                        We beseech thee, O Lord, pour thy grace into our hearts: that, as we have known the Incarnation of thy Son Jesus Christ by the message of an Angel, so by his Cross and Passion we may be brought to the glory of his Resurrection; through the same Christ our Lord. **Amen.**`,
                    });
                } else {
                    items.push({
                        type: "text",
                        content: `After childbirth thou didst remain a pure Virgin:

                        **Mother of God, intercede for us.**`,
                    });
                    items.push({
                        type: "text",
                        content: `Let us pray.

                        O God, who, by the fruitful virginity of blessed Mary, hast bestowed upon mankind the reward of eternal salvation: grant, we beseech thee, that we may experience her intercession, through whom we have been made worthy to receive the Author of Life; our Lord Jesus Christ, thy Son. **Amen.**`,
                    });
                }
            } else if (isBeforeEaster) {
                items.push({
                    type: "gabc chant",
                    gabc: aveReginaCaelorumEnglish,
                    title: "Ave Regina cælorum",
                    dropCap: true,
                    tone: "6",
                    playButtons: [
                        {
                            gabc: aveReginaCaelorumEnglish,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: aveReginaCaelorumEnglish,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({
                    type: "minimal gabc chant",
                    gabc: "(c3) Grant(h) that(h) I(h) may(h) praise(h) thee,(h) sa(h)cred(h) Vir(h)gin.(f) (::) *Give*(h) *me*(h) *strength*(h) *a*(h)*gainst*(h) *thine*(h) *e*(h)*ne*(f)*mies.*(f) (::) Let(h) us(f) pray.(g) (:) Grant(h) us(h) pro(h)tec(h)tion(h) in(h) our(h) weak(h)ness,(h) O(h) mer(h)ci(h)ful(h) God,(f) (;) that(h) we(h) who(h) com(h)me(h)mo(h)rate(h) the(h) ho(h)ly(h) Mo(h)ther(g) of(f) God(h) (;) may(h) tri(h)umph(h) o(h)ver(h) our(h) i(h)ni(h)qui(h)ties(h) with(h) the(h) help(h) of(h) her(h) in(h)ter(h)ces(h)sion;(d) (:) through(h) Christ(h) our(f) Lord.(g) (::) *A*(g)*men.*(h) (::)",
                    dropCap: false,
                    tone: "1",
                });
            } else if (isEastertide) {
                items.push({
                    type: "gabc chant",
                    gabc: reginaCaeliEnglish,
                    title: "Regina cæli",
                    dropCap: true,
                    tone: "6",
                    playButtons: [
                        {
                            gabc: reginaCaeliEnglish,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: reginaCaeliEnglish,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({
                    type: "minimal gabc chant",
                    gabc: "(c3) Re(h)joice(h) and(h) be(h) glad,(h) O(h) Vir(h)gin(h) Ma(h)ry,(h) al(h)le(h)lu(h)ia.(f) (::) *For*(h) *the*(h) *Lord*(h) *is*(h) *ri*(h)*sen*(h) *in*(h)*deed,*(h) *al*(h)*le*(h)*lu*(h)*ia.*(f) (::) Let(h) us(f) pray.(g) (:) O(h) God,(h) who,(h) by(h) the(h) Re(h)sur(h)rec(h)tion(h) of(h) thy(h) Son,(h) our(h) Lord(h) Je(h)sus(h) Christ,(h) hast(h) brought(h) joy(h) to(h) the(h) world:(f) (;) grant(h) we(h) be(h)seech(h) thee,(h) that,(h) through(h) his(h) Mo(h)ther(h) the(h) Vir(g)gin(f) Ma(h)ry,(h) (;) we(h) may(h) ob(h)tain(h) the(h) joys(h) of(h) e(h)ver(h)last(h)ing(h) life;(d) (:) through(h) the(h) same(h) Christ(h) our(f) Lord.(g) (::) *A*(g)*men.*(h) (::)",
                    dropCap: false,
                    tone: "1",
                });
            } else {
                items.push({
                    type: "gabc chant",
                    gabc: salveReginaEnglish,
                    title: "Salve Regina",
                    dropCap: true,
                    tone: "1",
                    playButtons: [
                        {
                            gabc: salveReginaEnglish,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: salveReginaEnglish,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({
                    type: "text",
                    content: `Pray for us, O holy Mother of God.

**That we would be made worthy of the promises of Christ.**`,
                });
                items.push({
                    type: "text",
                    content: `Let us pray.

Almighty, everlasting God, who by the cooperation of the Holy Spirit, didst prepare the body and soul of the glorious Virgin Mother Mary to become a worthy dwelling for thy Son; grant that we who rejoice in her commemoration may, by her loving intercession, be delivered from present evils and from eternal death; through the same Christ our Lord. **Amen.**`,
                });
            }
        } else {
            if (
                isAdvent ||
                day.month === 1 ||
                (day.month === 2 && day.date <= 2)
            ) {
                items.push({
                    type: "gabc chant",
                    gabc: almaLatin,
                    title: "Alma Redemptoris Mater",
                    dropCap: true,
                    tone: "5",
                    playButtons: [
                        {
                            gabc: almaLatin,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: almaLatin,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                if (isAdvent) {
                    items.push({
                        type: "text",
                        content: `Ángelus Dómini nuntiávit Maríæ.

                        **Et concépit de Spíritu Sancto.**`,
                    });
                    items.push({
                        type: "text",
                        content: `Orémus.

                        Grátiam tuam, quǽsumus, Dómine, méntibus nostris infúnde: ut, qui, Ángelo nuntiánte, Christi Fílii tui incarnatiónem cognóvimus; per passiónem eius et crucem, ad resurrectiónis glóriam perducámur. Per eúndem Christum Dóminum nostrum. **Amen.**`,
                    });
                } else {
                    items.push({
                        type: "text",
                        content: `Post partum, Virgo, invioláta permansísti.

                        **Dei Génetrix, intercéde pro nobis.**`,
                    });
                    items.push({
                        type: "text",
                        content: `Orémus.

                        Deus, qui salútis ætérnæ, beátæ Maríæ virginitáte fecúnda, humáno géneri prǽmia præstitísti: tríbue, quǽsumus; ut ipsam pro nobis intercédere sentiámus, per quam merúimus auctórem vitæ suscípere, Dóminum nostrum Iesum Christum Fílium tuum. **Amen.**`,
                    });
                }
            } else if (isBeforeEaster) {
                items.push({
                    type: "gabc chant",
                    gabc: aveReginaCaelorumLatin,
                    title: "Ave Regina cælorum",
                    dropCap: true,
                    tone: "6",
                    playButtons: [
                        {
                            gabc: aveReginaCaelorumLatin,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: aveReginaCaelorumLatin,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({
                    type: "minimal gabc chant",
                    gabc: "(c3) Dig(h)na(h)re(h) me(h) lau(h)da(h)re te,(h) Vir(h)go(h) sa(h)cra(h)ta.(f) (::) *Da*(h) *mi*(h)*hi*(h) *vir*(h)*tu*(h)*tem*(h) *con*(h)*tra*(h) *hos*(h)*tes*(h) *tu*(h)*os.*(f) (::) O(h)re(h)mus.(f) (:) Con(h)ce(h)de,(h) mi(h)se(h)ri(h)cors(h) De(h)us,(h) fra(h)gi(h)li(h)ta(h)ti(h) nos(h)trae(h) prae(h)si(h)di(f)um:(f) (;) ut,(h) qui(h) sanc(h)tae(h) De(h)i(h) Ge(h)ne(h)tri(h)cis(h) me(h)mo(h)ri(g)am(f) a(h)gi(h)mus;(h) (;) in(h)ter(h)ces(h)si(h)o(h)nis(h) ei(h)us(h) aux(h)i(h)li(h)o,(h) a(h) nos(h)tris(h) i(h)ni(h)qui(h)ta(h)ti(h)bus(h) re(h)sur(h)ga(h)mus.(d) (:) Per(h) Chris(h)tum(h) Do(h)mi(h)num(h) nos(h)trum.(f) (::) *A*(g)*men.*(h) (::)",
                    dropCap: false,
                    tone: "1",
                });
            } else if (isEastertide) {
                items.push({
                    type: "gabc chant",
                    gabc: reginaCaeliLatin,
                    title: "Regina cæli",
                    dropCap: true,
                    tone: "6",
                    playButtons: [
                        {
                            gabc: reginaCaeliLatin,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: reginaCaeliLatin,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({
                    type: "minimal gabc chant",
                    gabc: "(c3) Gau(h)de(h) et(h) læ(h)ta(h)re,(h) Vir(h)go(h) Ma(h)ri(h)a,(h) al(h)le(h)lu(h)ia.(f) (::) *Qui*(h)*a*(h) *sur*(h)*rex*(h)*it*(h) *Do*(h)*mi*(h)*nus*(h) *ve*(h)*re,*(h) *al*(h)*le*(h)*lu*(h)*ia.*(f) (::) O(h)re(h)mus.(f) (:) De(h)us,(h) qui(h) per(h) re(h)sur(h)rec(h)ti(h)o(h)nem(h) Fi(h)li(h)i(h) tu(h)i(h) Do(h)mi(h)ni(h) nos(h)tri(h) Ie(h)su(h) Chris(h)ti,(h) mun(h)dum(h) læ(h)ti(h)fi(h)ca(h)re(h) dig(h)na(h)tus(f) es:(f) (;) præ(h)sta,(h) quæ(h)su(h)mus,(h) ut,(h) per(h) ei(h)us(h) Ge(h)ne(h)tri(h)cem(h) Vir(h)gi(h)nem(g) Ma(f)ri(h)am,(h) (;) per(h)pe(h)tu(h)æ(h) ca(h)pi(h)a(h)mus(h) gau(h)di(h)a(h) vi(h)tæ.(d) (:) Per(h) Chris(h)tum(h) Do(h)mi(h)num(h) nos(h)trum.(f) (::) *A*(g)*men.*(h) (::)",
                    dropCap: false,
                    tone: "1",
                });
            } else {
                items.push({
                    type: "gabc chant",
                    gabc: salveReginaLatin,
                    title: "Salve Regina",
                    dropCap: true,
                    tone: "5",
                    playButtons: [
                        {
                            gabc: salveReginaLatin,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: salveReginaLatin,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({
                    type: "text",
                    content: `Ora pro nobis, sancta Dei Génitrix.

**Ut digni efficiámur promissiónibus Christi.**`,
                });
                items.push({
                    type: "text",
                    content: `Orémus.

Omnípotens sempitérne Deus, qui gloriósæ Vírginis Matris Maríæ corpus et ánimam, ut dignum Fílii tui habitáculum éffici mererétur, Spíritu Sancto cooperánte, præparásti: da, ut, cujus commemoratióne lætámur, ejus pia intercessióne, ab instántibus malis et a morte perpétua liberémur. Per eúmdem Christum Dóminum nostrum. **Amen.**`,
                });
            }
        }
    } else {
        items.push({
            type: "rubric",
            content:
                "No Marian Anthem is sung during the days of the Sacred Paschal Triduum.",
        });
    }

    items.push({ type: "spacer", big: true });

    return items;
};
